import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  initOfflineDB,
  savePendingOrder,
  getPendingOrders,
  getPendingOrderCount,
  markOrderSynced,
  deleteSyncedOrder,
  clearSyncedOrders,
  cacheMenuItems,
  getCachedMenu,
  getSyncQueue,
  removeFromSyncQueue
} from '../utils/offlineStorage';

const SYNC_INTERVAL = 30000; // 30 seconds
const HEALTH_CHECK_INTERVAL = 10000; // 10 seconds

/**
 * Custom hook for managing offline mode
 * Detects connectivity, queues orders, and handles sync
 */
export default function useOfflineMode(api) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]);
  
  const syncIntervalRef = useRef(null);
  const healthCheckIntervalRef = useRef(null);

  // Combined offline status (network offline OR server unreachable)
  const isOffline = !isOnline || !isServerReachable;

  /**
   * Check if the backend server is reachable
   */
  const checkServerHealth = useCallback(async () => {
    if (!api) return;
    
    try {
      // Try to reach the health endpoint
      const response = await api.get('/health', { timeout: 5000 });
      setIsServerReachable(response.status === 200);
    } catch (error) {
      console.log('Server health check failed:', error.message);
      setIsServerReachable(false);
    }
  }, [api]);

  /**
   * Update pending order count
   */
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingOrderCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Error getting pending count:', error);
    }
  }, []);

  /**
   * Save an order for offline sync
   */
  const queueOrder = useCallback(async (orderData) => {
    try {
      const savedOrder = await savePendingOrder({
        ...orderData,
        queuedAt: new Date().toISOString()
      });
      await updatePendingCount();
      toast.info('Order saved locally - will sync when online', {
        icon: '📴'
      });
      return savedOrder;
    } catch (error) {
      console.error('Error queuing order:', error);
      toast.error('Failed to save order locally');
      throw error;
    }
  }, [updatePendingCount]);

  /**
   * Sync a single order to the server
   */
  const syncOrder = useCallback(async (order) => {
    if (!api) return null;

    try {
      // Create the order on the server
      const response = await api.post('/orders/create', {
        ...order.orderData,
        offline_local_id: order.localId,
        offline_queued_at: order.createdAt
      });

      if (response.data?.order_id) {
        await markOrderSynced(order.localId, response.data.order_id);
        return { success: true, orderId: response.data.order_id };
      }
      
      return { success: false, error: 'No order ID returned' };
    } catch (error) {
      console.error('Error syncing order:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message 
      };
    }
  }, [api]);

  /**
   * Sync all pending orders
   */
  const syncAllPendingOrders = useCallback(async () => {
    if (isOffline || isSyncing) return;

    setIsSyncing(true);
    const errors = [];
    let syncedCount = 0;

    try {
      const pendingOrders = await getPendingOrders();
      
      if (pendingOrders.length === 0) {
        setIsSyncing(false);
        return { synced: 0, failed: 0 };
      }

      console.log(`Syncing ${pendingOrders.length} pending orders...`);

      for (const order of pendingOrders) {
        const result = await syncOrder(order);
        
        if (result.success) {
          syncedCount++;
          // Optionally delete synced orders
          await deleteSyncedOrder(order.localId);
        } else {
          errors.push({
            localId: order.localId,
            error: result.error
          });
        }
      }

      setLastSyncTime(new Date());
      setSyncErrors(errors);
      await updatePendingCount();

      if (syncedCount > 0) {
        toast.success(`Synced ${syncedCount} order${syncedCount > 1 ? 's' : ''}`, {
          icon: '✅'
        });
      }

      if (errors.length > 0) {
        toast.warning(`${errors.length} order${errors.length > 1 ? 's' : ''} failed to sync`);
      }

      return { synced: syncedCount, failed: errors.length };
    } catch (error) {
      console.error('Error during sync:', error);
      toast.error('Sync failed - will retry');
      return { synced: 0, failed: -1 };
    } finally {
      setIsSyncing(false);
    }
  }, [isOffline, isSyncing, syncOrder, updatePendingCount]);

  /**
   * Manual sync trigger
   */
  const triggerSync = useCallback(async () => {
    if (isOffline) {
      toast.error('Cannot sync while offline');
      return;
    }
    
    toast.info('Starting sync...');
    await syncAllPendingOrders();
  }, [isOffline, syncAllPendingOrders]);

  /**
   * Cache menu items for offline use
   */
  const cacheMenu = useCallback(async (restaurantId, menuItems) => {
    try {
      await cacheMenuItems(restaurantId, menuItems);
      console.log('Menu cached for offline use');
    } catch (error) {
      console.error('Error caching menu:', error);
    }
  }, []);

  /**
   * Get cached menu for offline use
   */
  const getOfflineMenu = useCallback(async (restaurantId) => {
    try {
      return await getCachedMenu(restaurantId);
    } catch (error) {
      console.error('Error getting cached menu:', error);
      return null;
    }
  }, []);

  // Initialize database
  useEffect(() => {
    initOfflineDB()
      .then(() => updatePendingCount())
      .catch(console.error);
  }, [updatePendingCount]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online!', { icon: '🌐' });
      // Trigger sync when coming back online
      setTimeout(syncAllPendingOrders, 1000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline - orders will be saved locally', {
        icon: '📴',
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncAllPendingOrders]);

  // Server health check interval
  useEffect(() => {
    if (!api) return;

    // Initial check
    checkServerHealth();

    // Set up interval
    healthCheckIntervalRef.current = setInterval(checkServerHealth, HEALTH_CHECK_INTERVAL);

    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, [api, checkServerHealth]);

  // Auto-sync interval when online
  useEffect(() => {
    if (isOffline) {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      return;
    }

    // Start sync interval
    syncIntervalRef.current = setInterval(() => {
      if (pendingCount > 0) {
        syncAllPendingOrders();
      }
    }, SYNC_INTERVAL);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isOffline, pendingCount, syncAllPendingOrders]);

  return {
    // Status
    isOnline,
    isServerReachable,
    isOffline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    syncErrors,
    
    // Actions
    queueOrder,
    triggerSync,
    cacheMenu,
    getOfflineMenu,
    updatePendingCount,
    
    // Manual sync
    syncAllPendingOrders
  };
}
